import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

function StatCard({ label, value, testId }) {
  return (
    <div className="border border-ink/10 rounded-lg p-3 bg-white/50" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wide text-ink-soft mb-1">{label}</p>
      <p className="text-lg font-serif text-ink">{value}</p>
    </div>
  );
}

/**
 * Clients Restructure — Team 360 Overview. Every action navigates to
 * the owning tab (never opens a modal here) — mirrors Client 360's
 * own OverviewTab, which does the exact same "navigate, don't
 * duplicate" pattern for Prepare Next Session.
 */
export default function OverviewTab({ overview }) {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [showAllTasks, setShowAllTasks] = useState(false);
  const goto = (tab) => navigate(`/rehearsal-room/coach/team/${workspaceId}/${tab}`);
  const visibleTasks = showAllTasks ? overview.open_tasks : overview.open_tasks.slice(0, 5);

  return (
    <div data-testid="team-360-overview-tab" className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Members" value={overview.member_count} testId="team-overview-member-count" />
        <StatCard
          label="Next team session"
          value={overview.next_session_at ? new Date(overview.next_session_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "None"}
          testId="team-overview-next-session"
        />
        <StatCard label="Available credits" value={overview.available_credits} testId="team-overview-available-credits" />
        <StatCard label="Sessions completed" value={overview.completed_session_count} testId="team-overview-completed-count" />
        <StatCard label="Current Journey stage" value={overview.current_stage_label || "Not set"} testId="team-overview-current-stage" />
        <StatCard label="Current/Next Practice" value={overview.next_practice_label || overview.current_practice_label || "None"} testId="team-overview-current-practice" />
      </div>

      <div className="border border-ink/10 rounded-lg p-4 bg-white/50">
        <h2 className="font-serif text-lg text-ink mb-3">Open team tasks</h2>
        {overview.open_task_count === 0 ? (
          <p className="text-sm text-ink-soft" data-testid="team-overview-tasks-empty">No open tasks for this team.</p>
        ) : (
          <>
            <ul className="space-y-1.5" data-testid="team-overview-tasks-list">
              {visibleTasks.map((t) => (
                <li key={t.ctk_id} className="text-sm text-ink" data-testid={`team-overview-task-${t.ctk_id}`}>{t.reason}</li>
              ))}
            </ul>
            {overview.open_tasks.length > 5 && (
              <button
                onClick={() => setShowAllTasks((v) => !v)}
                className="text-xs text-terracotta font-medium mt-2 hover:underline"
                data-testid="team-overview-tasks-toggle"
              >
                {showAllTasks ? "Show fewer" : `Show all ${overview.open_tasks.length}`}
              </button>
            )}
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => goto("prep")}
          className="text-sm font-medium bg-terracotta text-paper px-4 py-2 rounded-full hover:bg-terracotta/90 transition-colors"
          data-testid="team-overview-prepare-next-session"
        >
          Prepare Next Session
        </button>
        <button
          onClick={() => goto("sessions")}
          className="text-sm font-medium border border-ink/15 px-4 py-2 rounded-full text-ink hover:bg-ink/5 transition-colors"
          data-testid="team-overview-book-session"
        >
          Book Session
        </button>
        <button
          onClick={() => goto("practice")}
          className="text-sm font-medium border border-ink/15 px-4 py-2 rounded-full text-ink hover:bg-ink/5 transition-colors"
          data-testid="team-overview-assign-practice"
        >
          Assign Practice
        </button>
      </div>
    </div>
  );
}
